/**
 * FollowPatientModal Component
 * 
 * Modal for following a patient with users and groups
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { usersAPI } from '@/services/api';
import { useFollowers } from '@/hooks/useFollowers';
import { AddFollowerData } from '@/types/follower';
import { Users, UserCheck } from 'lucide-react';

interface FollowPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | number;
  patientName?: string;
}

// Mock groups - TODO: Replace with groups API when available
const mockGroups = [
  { id: 'group-1', name: 'Cardiology Team', description: 'Cardiology department team members' },
  { id: 'group-2', name: 'Primary Care Team', description: 'Primary care providers' },
  { id: 'group-3', name: 'Nursing Team', description: 'Nursing staff' },
];

export const FollowPatientModal: React.FC<FollowPatientModalProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
}) => {
  const [followAsMyself, setFollowAsMyself] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { followers, add, isAdding } = useFollowers({ patientId, enabled: isOpen });

  const currentUserId = 'current-user-id'; // TODO: Get from auth context

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      // Reset selections when modal opens
      setFollowAsMyself(false);
      setSelectedUsers([]);
      setSelectedGroups([]);
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await usersAPI.getAll();
      
      const data = response.data as { success: boolean; data: any };
      if (data.success && data.data) {
        if (data.data.data && Array.isArray(data.data.data)) {
          setUsers(data.data.data.filter((u: any) => u.active !== false));
        } else if (Array.isArray(data.data)) {
          setUsers(data.data.filter((u: any) => u.active !== false));
        } else {
          setUsers([]);
        }
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroups((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleConfirm = async () => {
    const followersToAdd: AddFollowerData[] = [];

    // Add "follow as myself" if selected
    if (followAsMyself) {
      const currentUser = users.find((u) => u.id === currentUserId) || {
        id: currentUserId,
        name: 'Current User',
        role: 'User',
      };
      followersToAdd.push({
        type: 'user',
        userId: currentUser.id,
        name: currentUser.name,
        role: currentUser.role,
      });
    }

    // Add selected users
    selectedUsers.forEach((userId) => {
      const user = users.find((u) => u.id === userId);
      if (user) {
        followersToAdd.push({
          type: 'user',
          userId: user.id,
          name: user.name || `${user.firstName} ${user.lastName}`,
          role: user.role,
        });
      }
    });

    // Add selected groups
    selectedGroups.forEach((groupId) => {
      const group = mockGroups.find((g) => g.id === groupId);
      if (group) {
        followersToAdd.push({
          type: 'group',
          groupId: group.id,
          name: group.name,
        });
      }
    });

    // Add all followers
    for (const followerData of followersToAdd) {
      add({ patientId, followerData });
    }

    // Close modal after a short delay to allow mutations to complete
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const hasSelections = followAsMyself || selectedUsers.length > 0 || selectedGroups.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Follow Patient${patientName ? `: ${patientName}` : ''}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Follow as myself */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-white">Follow as</h3>
          <Checkbox
            label="Follow as myself"
            checked={followAsMyself}
            onChange={(e) => setFollowAsMyself(e.target.checked)}
          />
        </div>

        {/* Users section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-medium text-white">Select Users</h3>
          </div>
          {isLoadingUsers ? (
            <div className="text-sm text-gray-400">Loading users...</div>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-700 rounded-lg p-3">
              {users.length === 0 ? (
                <div className="text-sm text-gray-400">No users available</div>
              ) : (
                users.map((user) => {
                  const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  return (
                    <div key={user.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                      />
                      <div className="flex-1">
                        <div className="text-sm text-white">{userName}</div>
                        {user.role && <div className="text-xs text-gray-400">{user.role}</div>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Groups section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-medium text-white">Select Groups</h3>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-700 rounded-lg p-3">
            {mockGroups.length === 0 ? (
              <div className="text-sm text-gray-400">No groups available</div>
            ) : (
              mockGroups.map((group) => (
                <div key={group.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedGroups.includes(group.id)}
                    onChange={() => handleToggleGroup(group.id)}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">{group.name}</div>
                    {group.description && (
                      <div className="text-xs text-gray-400">{group.description}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Current followers */}
        {followers.length > 0 && (
          <div className="space-y-2 pt-4 border-t border-gray-700">
            <h3 className="text-sm font-medium text-white">Currently Following ({followers.length})</h3>
            <div className="text-xs text-gray-400 space-y-1">
              {followers.map((follower) => (
                <div key={follower.id}>
                  {follower.name}
                  {follower.role && ` (${follower.role})`}
                  {follower.type === 'group' && ' [Group]'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!hasSelections || isAdding}
          >
            {isAdding ? 'Adding...' : 'Confirm'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};


